import { getRandomItemFromArray } from 'src/replicant/utils/random';
import type { Entity, QueriesObject, QueryResults, System } from '../../odie';

import { BlockComponent } from '../components/BlockComponent';
import { ChameleonBlockComponent } from '../components/ChameleonBlockComponent';
import { PhaseComponent } from '../components/PhaseComponent';
import { BasicBlockProps, blockPropsMap } from '../defs/block';
import { ChameleonBlockEntity } from '../entities/BlockEntity';
import type { GameScene } from '../GameScene';
import { ChameleonColor } from '../views/blocks/ChameleonBlockView';

/*
    handles chameleon block specifics
*/
export class ChameleonBlockSystem implements System {
  // constants
  //-------------------------------------------------------------------------
  public static readonly NAME = 'blockChameleon';
  public static Queries: QueriesObject = {
    block: {
      components: [BlockComponent, ChameleonBlockComponent],
      added: true,
    },
    phase: {
      components: [PhaseComponent],
      modified: true,
    },
  };

  // fields
  //-------------------------------------------------------------------------
  // injected
  public scene!: GameScene;
  public queries!: QueryResults;

  // impl
  //-------------------------------------------------------------------------
  public modifiedQuery(
    entity: Entity,
    component: PhaseComponent,
    properties: any,
  ) {
    const phase = this.scene.sessionEntity.c.phase;

    // if move input phase step chameleons
    if (
      properties.phase &&
      component.phase === 'input' &&
      phase.round > 0 &&
      component.moved
    ) {
      this._stepChameleon();
    }
  }

  public addedToQuery(entity: ChameleonBlockEntity) {
    const pool = this._getLevelAvailableColors();
    if (!pool) return;

    const comp = entity.c.blockChameleon;
    const initialColor = comp.activeColor;

    // verify whether initial chameleon colour exists within the spawnables on the level
    const valid = pool.includes(initialColor);

    // If valid, then no need to reassign anything
    if (valid) return;

    // Otherwise, assign a random available colour
    comp.activeColor = getRandomItemFromArray(pool, Math.random);
  }

  // private: actions
  //-------------------------------------------------------------------------
  private _stepChameleon() {
    const pool = this._getLevelAvailableColors();
    if (!pool) return;

    // rotate chameleon colors
    this.queries.block.forEach((entity: ChameleonBlockEntity) => {
      const currentColor = entity.c.blockChameleon.activeColor;
      const index = pool.indexOf(currentColor);
      const newColor = pool[(index + 1) % pool.length];
      entity.c.blockChameleon.activeColor = newColor;
    });
  }

  /**
   * grab the pool of basic blocks from the level spawn list
   */
  private _getLevelAvailableColors() {
    return this.scene.mapDef?.spawns
      .filter((id) => id.includes('basic'))
      .map((id) => {
        const props = blockPropsMap[id] as BasicBlockProps;
        return props.color as ChameleonColor;
      });
  }
}
